"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { useState } from "react";
import { useIsInsideAccordionItem } from "../../elements/accordion/accordion";
import { Button } from "../../elements/button/button";
import { Image } from "../../elements/image/image";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../elements/table/table";
import { Text } from "../../elements/text/text";
import { TextD2C } from "../../elements/text-d2c/text";
import { textD2CSizes } from "../../elements/text-d2c/text-d2c.constants";
import { Container } from "../../layout/grid";
import type { BlockInspectorProps } from "../../lib/inspector";
import { cn } from "../../lib/utils";

const geometryTableVariants = cva("", {
  variants: {
    backgroundColor: {
      black: "bg-black",
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
    },
    columnWidth: {
      // Mobile is full-width for readability; desktop width follows Contentful choice.
      8: "col-span-12 md:col-span-8 md:col-start-3",
      10: "col-span-12 md:col-span-10 md:col-start-2",
      12: "col-span-12",
    },
  },
  defaultVariants: {
    columnWidth: 8,
  },
});

export interface GeometryMeasurement {
  label: string;
  letter?: string;
  values: {
    [key: string]: string;
  };
  /** Optional inch values keyed by the same size identifiers. */
  valuesInches?: {
    [key: string]: string;
  };
}

export interface GeometryTableProps
  extends VariantProps<typeof geometryTableVariants> {
  mode?: "D2C" | "B2B";
  anchorId?: string;
  backgroundColor?: "black" | "white" | "whitesmoke";
  paddingTop?: "none" | "small" | "medium" | "large";
  paddingBottom?: "none" | "small" | "medium" | "large";
  columnWidth?: VariantProps<typeof geometryTableVariants>["columnWidth"];
  sizes: string[];
  measurements: GeometryMeasurement[];
  microcopies: {
    frameSize?: string;
    title?: string;
    sizesIn?: string;
    unitMm?: string;
    unitInches?: string;
  };
  showUnitToggle?: boolean;
  defaultUnit?: "mm" | "inches";
  /** Optional image URL for product geometry diagram */
  imageUrl?: string;
  /** Alt text for the geometry image */
  imageAlt?: string;
  /** Product code for API integration to fetch geometry parameters */
  productCodes?: string[];
  /** Product codes for API integration to fetch geometry parameters from first matching product */
  productCode?: string;
  inspectorProps?: BlockInspectorProps;
}

interface UnitToggleGroupProps {
  selectedUnit: "mm" | "inches";
  onSelectMm: () => void;
  onSelectInches: () => void;
  labelMm: string;
  labelInches: string;
  className?: string;
  mode: "D2C" | "B2B";
}

function UnitToggleGroup(props: Readonly<UnitToggleGroupProps>) {
  if (props.mode === "D2C") {
    return (
      <div className="flex justify-start md:justify-end">
        <div className="relative inline-flex rounded-sm border border-alabastergrey p-1.5">
          {/* Sliding pill — moves from mm to inches position via translateX */}
          <span
            aria-hidden="true"
            className={cn(
              "absolute top-1.5 bottom-1.5 left-1.5 w-18 rounded-sm bg-alabastergrey transition-transform duration-200 ease-in-out",
              props.selectedUnit === "inches" && "translate-x-full"
            )}
          />
          <Button
            aria-pressed={props.selectedUnit === "mm"}
            className={cn(
              "relative z-1 w-18 flex-1 cursor-pointer px-4 py-2 md:py-1",
              textD2CSizes["p-small"]
            )}
            mode={props.mode}
            onClick={props.onSelectMm}
            type="button"
            unstyled={true}
            variant="ghost"
          >
            {props.labelMm}
          </Button>
          <Button
            aria-pressed={props.selectedUnit === "inches"}
            className={cn(
              "relative z-1 w-18 flex-1 cursor-pointer px-4 py-2 md:py-1",
              textD2CSizes["p-small"]
            )}
            mode={props.mode}
            onClick={props.onSelectInches}
            type="button"
            unstyled={true}
            variant="ghost"
          >
            {props.labelInches}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative inline-flex h-9 w-32 items-center gap-1 p-0",
        props.className
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute top-0 bottom-0 left-0 rounded-md bg-alabastergrey shadow-sm transition-all duration-200 ease-in-out",
          props.selectedUnit === "inches" ? "w-16 translate-x-12" : "w-12"
        )}
      />
      <Button
        aria-pressed={props.selectedUnit === "mm"}
        className={cn(
          "relative z-10 flex h-9 w-12 items-center justify-center gap-2 rounded-md border border-alabastergrey px-3 py-2 text-black transition-colors hover:bg-alabastergrey focus:outline-none focus-visible:ring-2 focus-visible:ring-alabastergrey focus-visible:ring-offset-0",
          props.selectedUnit === "mm" ? "bg-alabastergrey" : "bg-white",
          textD2CSizes["p-small"]
        )}
        mode={props.mode}
        onClick={props.onSelectMm}
        type="button"
        variant="secondary"
      >
        <Text>{props.labelMm}</Text>
      </Button>
      <Button
        aria-pressed={props.selectedUnit === "inches"}
        className={cn(
          "relative z-10 flex h-9 w-16 items-center justify-center gap-2 rounded-md border border-alabastergrey px-3 py-2 text-black transition-colors hover:bg-alabastergrey focus:outline-none focus-visible:ring-2 focus-visible:ring-alabastergrey focus-visible:ring-offset-0",
          props.selectedUnit === "inches" ? "bg-alabastergrey" : "bg-white",
          textD2CSizes["p-small"]
        )}
        mode={props.mode}
        onClick={props.onSelectInches}
        type="button"
        variant="secondary"
      >
        <Text>{props.labelInches}</Text>
      </Button>
    </div>
  );
}

// table-fixed keeps column widths independent of cell content, so the value
// columns don't shift when toggling between mm and inches. The label column is
// the widest (a quarter of the table); the value columns split the remaining
// space evenly and are right-aligned.
const LABEL_COLUMN_CLASS = "w-1/2 md:w-1/4";
const VALUE_COLUMN_CLASS = "text-right";

export function GeometryTable({
  backgroundColor,
  columnWidth,
  sizes,
  measurements,
  microcopies,
  showUnitToggle = false,
  imageUrl,
  imageAlt,
  mode = "D2C",
  ...props
}: Readonly<GeometryTableProps>) {
  const isInsideAccordion = useIsInsideAccordionItem();
  const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
  const [selectedUnit, setSelectedUnit] = useState<"mm" | "inches">(
    props.defaultUnit ?? "mm"
  );
  const getCellValue = (measurement: GeometryMeasurement, size: string) => {
    if (selectedUnit === "inches" && measurement.valuesInches) {
      return measurement.valuesInches[size] ?? "-";
    }
    return measurement.values[size] ?? "-";
  };

  const unitToggleProps = {
    selectedUnit,
    onSelectMm: () => setSelectedUnit("mm"),
    onSelectInches: () => setSelectedUnit("inches"),
    labelMm: microcopies.unitMm || "mm",
    labelInches: microcopies.unitInches || "inches",
    mode,
  };
  const surfaceBackgroundClass =
    backgroundColor === "whitesmoke" ? "bg-whitesmoke" : "bg-white";

  const renderTableHeader = () => {
    if (mode === "D2C") {
      return (
        <>
          {/* Header: Hidden on mobile, shown on desktop */}
          <TableHeader className="hidden md:table-header-group">
            <TableRow className={"max-md:hover:bg-transparent"}>
              <TableHead
                className={cn(
                  "sticky left-0 z-10 whitespace-nowrap bg-inherit py-3 text-left font-bold font-brand",
                  LABEL_COLUMN_CLASS,
                  textD2CSizes.p
                )}
              >
                {microcopies.frameSize || ""}
              </TableHead>

              {/* Show all sizes - scrollable on mobile, normal on desktop */}
              {sizes.map((size) => (
                <TableHead
                  className={cn(
                    "whitespace-nowrap px-4 py-3 font-bold font-brand",
                    VALUE_COLUMN_CLASS,
                    textD2CSizes.p
                  )}
                  key={`header-${size}`}
                >
                  <div className="font-bold">{size}</div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
        </>
      );
    }

    return (
      <>
        {/* Header: Hidden on mobile, shown on desktop */}
        <TableHeader className="hidden md:table-header-group">
          <TableRow className={"max-md:hover:bg-transparent"}>
            <TableHead
              className={cn(
                "sticky left-0 z-10 whitespace-nowrap bg-inherit px-4 py-3 text-left font-bold font-brand",
                LABEL_COLUMN_CLASS,
                textD2CSizes.p
              )}
            >
              {microcopies.frameSize || ""}
            </TableHead>
            {/* Show all sizes - scrollable on mobile, normal on desktop */}
            {sizes.map((size) => (
              <TableHead
                className={cn(
                  "whitespace-nowrap px-4 py-3 text-center font-bold font-brand",
                  VALUE_COLUMN_CLASS,
                  textD2CSizes.p
                )}
                key={`header-${size}`}
              >
                <div className="flex items-center justify-center">
                  <Text variant="body-sm-bold">{size}</Text>
                </div>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
      </>
    );
  };

  const renderMeasurementRow = (measurement: GeometryMeasurement) => {
    if (mode === "D2C") {
      return (
        <TableRow className={""} key={measurement.label}>
          <TableCell
            className={cn(
              "whitespace-wrap bg-inherit py-3 text-left md:sticky md:left-0 md:z-10",
              LABEL_COLUMN_CLASS
            )}
          >
            <div className="flex items-center gap-2">
              {measurement.letter && (
                <div className="flex aspect-square h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-zinc-500">
                  <TextD2C color="white" variant="p-small">
                    {measurement.letter}
                  </TextD2C>
                </div>
              )}
              {measurement.label}
            </div>
          </TableCell>

          {/* Desktop: Show all sizes */}
          {sizes.map((size) => (
            <TableCell
              className={cn(
                "hidden whitespace-nowrap px-4 py-3 md:table-cell",
                VALUE_COLUMN_CLASS
              )}
              key={`${measurement.label}-${size}`}
            >
              {getCellValue(measurement, size)}
            </TableCell>
          ))}

          {/* Mobile: Show only selected size - no horizontal scroll */}
          <TableCell
            className={cn(
              "whitespace-nowrap px-4 py-3 md:hidden",
              VALUE_COLUMN_CLASS
            )}
          >
            {getCellValue(measurement, sizes[selectedSizeIndex])}
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow className={""} key={measurement.label}>
        <TableCell
          className={cn(
            "whitespace-nowrap bg-inherit px-4 py-3 text-left md:sticky md:left-0 md:z-10",
            LABEL_COLUMN_CLASS
          )}
        >
          <div className="flex items-center gap-2">
            {measurement.letter && (
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-500">
                <Text color="white" variant="body-sm-bold">
                  {measurement.letter}
                </Text>
              </div>
            )}
            <Text color="muted" variant="body-sm-bold">
              {measurement.label}
            </Text>
          </div>
        </TableCell>

        {/* Desktop: Show all sizes */}
        {sizes.map((size) => (
          <TableCell
            className={cn(
              "hidden whitespace-nowrap px-4 py-3 text-center md:table-cell",
              VALUE_COLUMN_CLASS
            )}
            key={`${measurement.label}-${size}`}
          >
            {getCellValue(measurement, size)}
          </TableCell>
        ))}

        {/* Mobile: Show only selected size - no horizontal scroll */}
        <TableCell
          className={cn(
            "whitespace-nowrap px-4 py-3 text-center md:hidden",
            VALUE_COLUMN_CLASS
          )}
        >
          {getCellValue(measurement, sizes[selectedSizeIndex])}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Container
      className={cn(
        geometryTableVariants({ backgroundColor, columnWidth: null }),
        "component-geometry-table"
      )}
      fullWidth={true}
      id={props.anchorId}
      outerContainerOnly={isInsideAccordion}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      {...props.inspectorProps?.container}
    >
      <div
        className={cn(
          "block min-w-0",
          geometryTableVariants({ backgroundColor: null, columnWidth })
        )}
      >
        {/* Image section - constrained width with proper spacing */}
        <div
          className={cn(
            "mb-8 flex flex-shrink-0 justify-center overflow-hidden md:overflow-visible md:bg-transparent",
            surfaceBackgroundClass
          )}
        >
          {imageUrl && (
            <div
              className={cn(
                "relative aspect-[750/547] w-full md:max-w-3xl",
                surfaceBackgroundClass
              )}
            >
              <Image
                alt={imageAlt || "Bike frame geometry diagram"}
                aspectRatio={"4:3"}
                className="absolute inset-0 h-full w-full object-contain"
                height={547}
                sizes="(max-width: 768px) 100vw, 750px"
                src={imageUrl || ""}
                width={750}
              />
            </div>
          )}
        </div>

        {/* Table below image - constrained width */}
        <div
          className={cn(
            "sticky top-25 z-40 md:static md:top-auto md:z-auto md:mb-10 md:bg-transparent",
            surfaceBackgroundClass
          )}
        >
          {/* Mobile: Sizes in label with unit toggle inline (on right), then size buttons below */}
          <div className="flex flex-col gap-2 md:hidden">
            {showUnitToggle && (
              <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                <span className="flex h-12 items-center">
                  {microcopies.sizesIn && (
                    <Text color="black" variant="body-sm-bold">
                      {microcopies.sizesIn}
                    </Text>
                  )}
                </span>
                <UnitToggleGroup {...unitToggleProps} />
              </div>
            )}

            {/* Scroll container: never wider than the viewport; tabs scroll horizontally when they don't fit */}
            <div className="w-full max-w-full overflow-x-auto border-stone-200 border-b [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex w-max">
                {sizes.map((size, index) => {
                  return (
                    <Button
                      className={cn(
                        // Figma: Tab Nav Link - flex row, center aligned, 56px height, 1.5px vertical padding
                        "flex h-14 w-14 shrink-0 cursor-pointer flex-row items-center justify-center rounded-none border-b-2 px-2 py-1.5 transition-all",
                        "!bg-transparent !shadow-none !outline-none !ring-0 text-base leading-6",
                        selectedSizeIndex === index
                          ? "border-black font-semibold text-stone-950"
                          : "border-transparent font-normal text-stone-500"
                      )}
                      key={`size-badge-${size}`}
                      mode={mode}
                      onClick={() => setSelectedSizeIndex(index)}
                      type="button"
                      variant="secondary"
                    >
                      {size}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Desktop: Original layout */}
          {showUnitToggle && (
            <div className="hidden flex-row items-center justify-end gap-3 md:flex">
              {microcopies.sizesIn && (
                <Text color="black" variant="body-md-bold">
                  {microcopies.sizesIn}
                </Text>
              )}
              <UnitToggleGroup {...unitToggleProps} />
            </div>
          )}
        </div>

        {/* Table wrapper - vertical scroll on mobile, fixed height */}
        <div
          className={cn(
            "max-h-96 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] md:relative md:z-10 md:max-h-none md:overflow-y-visible md:bg-transparent [&::-webkit-scrollbar]:hidden",
            surfaceBackgroundClass
          )}
        >
          <Table
            className={cn(
              "w-full table-fixed font-brand font-normal last:pb-0 only:mb-0",
              surfaceBackgroundClass,
              textD2CSizes["p-small"]
            )}
          >
            {renderTableHeader()}
            <TableBody>
              {measurements.map((measurement) =>
                renderMeasurementRow(measurement)
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Container>
  );
}
